import { ProcessingFacilityEmissionData } from 'models/ProcessingFacilityEmissionData/ProcessingFacilityEmissionData.model';
import { ProductDistributionEmissionData } from 'models/ProductDistributionEmissionData/ProductDistributionEmissionData.model';
import { ProductRawMaterial } from 'models/ProductRawMaterial/productRawMaterial.model';
import { ProductRawMaterialTransportations } from 'models/ProductRawMaterialTransportations/productRawMaterialTransportations.model';
import React, { createContext, useState, useContext } from 'react';

// Define the type for the context
type MyContextType = {
  stage1Data: ProductRawMaterial[];
  stage1Total: number | null;
  stage2Data: ProductRawMaterialTransportations[];
  stage2Total: number | null;
  stage3Data: ProcessingFacilityEmissionData | null;
  stage3Total: number | null;
  stage4Data: ProductDistributionEmissionData[] | null;
  stage4Total: number | null;
  setStage1Data: (data: ProductRawMaterial[]) => void;
  setStage1Total: (total: number) => void;
  setStage2Data: (data: ProductRawMaterialTransportations[]) => void;
  setStage2Total: (total: number) => void;
  setStage3Data: (data: ProcessingFacilityEmissionData) => void;
  setStage3Total: (total: number) => void;
  setStage4Data: (data: ProductDistributionEmissionData[]) => void;
  setStage4Total: (total: number) => void;
  b2b: number | null;
  setB2b: (data: number) => void;
  b2c: number | null;
  setB2c: (data: number) => void;
};

// Create the context
export const EmissionDataContext = createContext<MyContextType>({
  stage1Data: [],
  stage1Total: null,
  stage2Data: [],
  stage2Total: null,
  stage3Data: null,
  stage3Total: null,
  stage4Data: null,
  stage4Total: null,
  b2b: null,
  b2c: null,
  setStage1Data: (data: ProductRawMaterial[]) => {},
  setStage1Total: (total: number) => {},
  setStage2Data: (data: ProductRawMaterialTransportations[]) => {},
  setStage2Total: (total: number) => {},
  setStage3Data: (data: ProcessingFacilityEmissionData) => {},
  setStage3Total: (total: number) => {},
  setStage4Data: (data: ProductDistributionEmissionData[]) => {},
  setStage4Total: (total: number) => {},
  setB2b: (value: number) => {},
  setB2c: (value: number) => {},
});

// Create a custom hook to access the context
export const useMyContext = () => {
  const context = useContext(EmissionDataContext);
  if (!context) {
    throw new Error('useMyContext must be used within a MyContextProvider');
  }
  return context;
};

// Create the context provider component
export const EmissionContextProvider: React.FC = ({ children }) => {
  const [stage1Data, setStage1Data] = useState<ProductRawMaterial[]>([]);
  const [stage2Data, setStage2Data] = useState<
    ProductRawMaterialTransportations[]
  >([]);
  const [stage3Data, setStage3Data] =
    useState<ProcessingFacilityEmissionData | null>(null);
  const [stage4Data, setStage4Data] = useState<
    ProductDistributionEmissionData[] | null
  >(null);
  const [stage1Total, setStage1Total] = useState<number | null>(null);
  const [stage2Total, setStage2Total] = useState<number | null>(null);
  const [stage3Total, setStage3Total] = useState<number | null>(null);
  const [stage4Total, setStage4Total] = useState<number | null>(null);
  const [b2b, setB2b] = useState<number | null>(null);
  const [b2c, setB2c] = useState<number | null>(null);

  return (
    <EmissionDataContext.Provider
      value={{
        stage1Data,
        stage2Data,
        stage3Data,
        stage4Data,
        stage1Total,
        stage2Total,
        stage3Total,
        stage4Total,
        b2b,
        b2c,
        setStage1Data,
        setStage2Data,
        setStage3Data,
        setStage4Data,
        setStage1Total,
        setStage2Total,
        setStage3Total,
        setStage4Total,
        setB2b,
        setB2c,
      }}>
      {children}
    </EmissionDataContext.Provider>
  );
};
